import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';

import {
  ItemDTO, CompilationFullDTO, CompilationThingDTO, CompilationLookDTO,
} from '@/api';
import service from '@/services';
import { throwError } from '@/utils';

export type Groups = {
  top2: CompilationThingDTO[],
  bottom: CompilationThingDTO[],
  top1: CompilationThingDTO[],
  outerwear: CompilationThingDTO[],
  dress: CompilationThingDTO[],
  shoes: CompilationThingDTO[],
  accessories: CompilationThingDTO[],
  bag: CompilationThingDTO[],
  jewelry: CompilationThingDTO[],
}

export type GroupedLook = CompilationLookDTO & {groups: Groups};

export function getTempRange(thing: ItemDTO): [number, number] {
  return [Number(thing.temperatureFrom), Number(thing.temperatureTo)];
}

@Module({ name: 'CompilationsModule', namespaced: true })
export default class CompilationsModule extends VuexModule {
  loading = false;

  model: CompilationFullDTO | null = null;

  get things() {
    return this.model?.things || [];
  }

  get looks() {
    return this.model?.looks || [];
  }

  get groupedThingsByLook(): GroupedLook[] {
    return (this.model?.looks || []).map((look) => {
      const getLookThings = (ids: number[]) => (this.model?.things || [])
        .filter(thing => ids.includes(thing.id as number));
      const groups: Groups = {
        top2: [],
        bottom: [],
        top1: [],
        outerwear: [],
        dress: [],
        shoes: [],
        accessories: [],
        bag: [],
        jewelry: [],
      };
      getLookThings(look.thingIds || []).forEach((item) => {
        const key = item.aiType as keyof typeof groups | undefined;
        if (!key) return;
        groups[key].push(item);
      });
      return {
        ...look,
        groups,
      };
    });
  }

  @Mutation
  setModel(value: CompilationFullDTO) {
    this.model = value;
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  // @Mutation
  // removeSandboxThing(id: number) {
  //   const index = this.compilationThings.findIndex(thing => thing.id === id);
  //   if (index > -1) {
  //     this.compilationThings.splice(index, 1);
  //   }
  // }

  @Action({ rawError: true })
  async fetchCompilation({ compilationId } : { compilationId: number}) {
    try {
      amplitude.getInstance().logEvent('Загрузка подборки клиента');
      this.setLoading(true);
      const { data } = await service.compilationsApi().compilationEndpointGetOrder(compilationId);
      this.setModel(data);
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки подборки клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addUserThingToLook({ compilationId, thingId } :
      { compilationId: number; thingId: number; }) {
    try {
      amplitude.getInstance().logEvent('Добавление вещи клиента в лук');
      this.setLoading(true);
      const { data: userThing } = await service.compilationsApi()
        .compilationEndpointAddThing(compilationId, undefined, thingId);
      const { data } = await service.compilationsApi()
        .compilationEndpointAddCompilationThingToLook(compilationId, userThing.id as number);
      await this.fetchCompilation({ compilationId });
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки списка вещей клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addUserThings({ compilationId, thingsId }: {compilationId: number, thingsId: number[]}) {
    try {
      amplitude.getInstance().logEvent('Добавление клиента');
      const promiseis = thingsId.map(id => service.compilationsApi()
        .compilationEndpointAddThing(compilationId, undefined, id)
        .then(({ data }) => data));
      const data = await Promise.all(promiseis);
      await this.fetchCompilation({ compilationId });
      return data;
    } catch (e) {
      return throwError('Ошибка переноса из песочницы в вещи клиента')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async assignStylist({ compilationId, stylistId }: {compilationId: number, stylistId: number}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Установлен стилист', stylistId);
      const { data } = await service.compilationsApi()
        .compilationEndpointAssignStylist(compilationId, stylistId);
      this.setModel(data);
      return data;
    } catch (e) {
      return throwError('Ошибка установления стилиста')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async saveStatus({ compilationId, status }: {compilationId: number, status: 'NEW' | 'IN_PROGRESS' | 'FINISHED'}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Установлен статус', status);
      const { data } = await service.compilationsApi()
        .compilationEndpointOrderStatus(compilationId, status);
      this.setModel(data);
      return data;
    } catch (e) {
      return throwError('Ошибка сохранения статуса')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async saveComment({ compilationId, stylistComment }:
      {compilationId: number, stylistComment: string}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Установлен комментария стилиста', stylistComment);
      const { data } = await service.compilationsApi()
        .compilationEndpointAddStylistComment(compilationId, stylistComment);
      this.setModel(data);
      return data;
    } catch (e) {
      return throwError('Ошибка сохранения комментария')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async saveDeadline({ compilationId, deadlineMillis }:
      {compilationId: number, deadlineMillis: number}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Установлен комментария стилиста', deadlineMillis);
      const { data } = await service.compilationsApi()
        .compilationEndpointOrderDeadline(compilationId, deadlineMillis);
      this.setModel(data);
      return data;
    } catch (e) {
      return throwError('Ошибка сохранения комментария')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async createLook(compilationId: number) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Создан образ в подборке');
      const { data: id } = await service.compilationsApi()
        .compilationEndpointSaveLook(compilationId);
      await this.fetchCompilation({ compilationId });
      return id;
    } catch (e) {
      return throwError('Ошибка создания образа')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async copyLook({ compilationId, compilationLookId }:
      {compilationId: number; compilationLookId: number;}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Образ удалён из подборки');
      await service.compilationsApi()
        .compilationEndpointCopyLook(compilationLookId);
      await this.fetchCompilation({ compilationId });
      return compilationLookId;
    } catch (e) {
      return throwError('Ошибка удаления образа')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async deleteLook({ compilationId, compilationLookId }:
      {compilationId: number; compilationLookId: number;}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Образ удалён из подборки');
      await service.compilationsApi()
        .compilationEndpointDeleteLook(compilationLookId);
      await this.fetchCompilation({ compilationId });
      return null;
    } catch (e) {
      return throwError('Ошибка удаления образа')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addClientThingToLook({ compilationId, compilationLookId, clientThingId }:
      {compilationId: number; compilationLookId: number; clientThingId:number;}) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Вещь клиента добавлена в образ');
      await service.compilationsApi()
        .compilationEndpointAddThing(compilationLookId, undefined, clientThingId);
      await this.fetchCompilation({ compilationId });
      return null;
    } catch (e) {
      return throwError('Ошибка добавления вещи клиента в образ')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async addThingToLook({ compilationId, compilationLookId, thingId }
    :{ compilationId: number; compilationLookId: number; thingId: number; }) {
    try {
      amplitude.getInstance().logEvent('Добавление вещи в лук');
      this.setLoading(true);
      await service.compilationsApi()
        .compilationEndpointAddCompilationThingToLook(compilationLookId, thingId);
      await this.fetchCompilation({ compilationId });
      return null;
    } catch (e) {
      return throwError('Ошибка добавления вещи в образ')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async deleteThingFromLook({ compilationId, compilationLookId, thingId } :
      { compilationId: number; compilationLookId: number; thingId: number; }) {
    try {
      amplitude.getInstance().logEvent('Удаление вещи из образа');
      this.setLoading(true);
      await service.compilationsApi()
        .compilationEndpointRemoveThingFromLook(compilationLookId, thingId);
      await this.fetchCompilation({ compilationId });
      return null;
    } catch (e) {
      return throwError('Ошибка удаления вещи из образа')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async deleteClientThingFromLook({ compilationId, compilationLookId, clientThingId } :
      { compilationId: number; compilationLookId: number; clientThingId: number; }) {
    try {
      const thing = this.model?.things?.find(t => t.clientItemId === clientThingId);
      amplitude.getInstance().logEvent('Удаление вещи из образа');
      this.setLoading(true);
      if (thing?.id != null) {
        await this.deleteThingFromLook({ compilationId, compilationLookId, thingId: thing.id });
        await service.compilationsApi()
          .compilationEndpointDeleteThing(thing.id);
      }
      await this.fetchCompilation({ compilationId });
      return null;
    } catch (e) {
      return throwError('Ошибка удаления вещи из клиента образа')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async deleteThing({ compilationId, thingId } :
      { compilationId: number; thingId: number; }) {
    this.setLoading(true);
    try {
      amplitude.getInstance().logEvent('Удаление вещи из подборки');
      await service.compilationsApi()
        .compilationEndpointDeleteThing(thingId);
      await this.fetchCompilation({ compilationId });
      return null;
    } catch (e) {
      return throwError('Ошибка удаления вещи')(e);
    } finally {
      this.setLoading(false);
    }
  }
}
